import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    FormControlLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip
} from '@mui/material';
import { Add, Edit, Block, CheckCircle } from '@mui/icons-material';
import api from '../../services/api';
import * as qs from 'qs';
import { format, parseISO } from 'date-fns';

const initialForm = {
    id: null,
    document: '',
    status: true,
    message: ''
};

export default function Controle() {
    const [controls, setControls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(initialForm);

    const [subscriberOptions, setSubscriberOptions] = useState([]);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [loadingSubscribers, setLoadingSubscribers] = useState(false);
    const subscriberSearchTimer = useRef(null);

    const [stateAlert, setStateAlert] = useState({
        status: false,
        type: 'success',
        msg: ''
    });

    async function loadControls() {
        setLoading(true);
        try {
            const params = {
                sort: ['createdAt:desc'],
                pagination: {
                    page: 1,
                    pageSize: 200
                },
                encodeValuesOnly: true
            };

            const queryString = qs.stringify(params);
            const response = await api.get(`/controls?${queryString}`);
            setControls(response.data?.data || []);
        } catch (error) {
            console.error('Erro ao carregar controles:', error);
            setStateAlert({
                status: true,
                type: 'error',
                msg: error.response?.data?.error?.message || 'Erro ao carregar controles'
            });
        } finally {
            setLoading(false);
        }
    }

    async function searchSubscribers(searchText) {
        if (!searchText || searchText.trim().length < 3) {
            setSubscriberOptions([]);
            return;
        }

        setLoadingSubscribers(true);
        try {
            const params = {
                filters: {
                    $or: [
                        { document: { $containsi: searchText } },
                        { name: { $containsi: searchText } }
                    ]
                },
                fields: ['name', 'document', 'email'],
                sort: ['name:asc'],
                pagination: {
                    page: 1,
                    pageSize: 20
                },
                encodeValuesOnly: true
            };

            const queryString = qs.stringify(params);
            const response = await api.get(`/subscribers?${queryString}`);
            setSubscriberOptions(response.data?.data || []);
        } catch (error) {
            console.error('Erro ao buscar subscribers:', error);
            setSubscriberOptions([]);
        } finally {
            setLoadingSubscribers(false);
        }
    }

    function openNewDialog() {
        setForm(initialForm);
        setSelectedSubscriber(null);
        setSubscriberOptions([]);
        setDialogOpen(true);
    }

    function openEditDialog(control) {
        setForm({
            id: control.id,
            document: control.attributes?.document || '',
            status: Boolean(control.attributes?.status),
            message: control.attributes?.message || ''
        });
        setSelectedSubscriber(null);
        setSubscriberOptions([]);
        setDialogOpen(true);
    }

    function closeDialog() {
        setDialogOpen(false);
        setForm(initialForm);
        setSelectedSubscriber(null);
        setSubscriberOptions([]);
    }

    async function handleSave() {
        if (!form.document) {
            setStateAlert({
                status: true,
                type: 'warning',
                msg: 'Selecione um subscriber para preencher o documento.'
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                data: {
                    document: form.document,
                    status: Boolean(form.status),
                    message: form.message || null
                }
            };

            if (form.id) {
                await api.put(`/controls/${form.id}`, payload);
                setStateAlert({
                    status: true,
                    type: 'success',
                    msg: 'Controle atualizado com sucesso!'
                });
            } else {
                await api.post('/controls', payload);
                setStateAlert({
                    status: true,
                    type: 'success',
                    msg: 'Controle criado com sucesso!'
                });
            }

            closeDialog();
            await loadControls();
        } catch (error) {
            console.error('Erro ao salvar controle:', error);
            setStateAlert({
                status: true,
                type: 'error',
                msg: error.response?.data?.error?.message || 'Erro ao salvar controle'
            });
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        loadControls();

        return () => {
            if (subscriberSearchTimer.current) {
                clearTimeout(subscriberSearchTimer.current);
            }
        };
    }, []);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }} p={3}>
            <Snackbar
                open={stateAlert.status}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ ...stateAlert, status: false })}
            >
                <Alert severity={stateAlert.type}>{stateAlert.msg}</Alert>
            </Snackbar>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold">
                    Controle de Subscribers
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openNewDialog}
                >
                    Novo Controle
                </Button>
            </Box>

            <Card>
                <CardContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={5}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Documento</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Mensagem</TableCell>
                                        <TableCell>Criado em</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {controls.map((control) => (
                                        <TableRow key={control.id}>
                                            <TableCell>
                                                {control.attributes?.document || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {control.attributes?.status ? (
                                                    <Chip
                                                        size="small"
                                                        icon={<Block />}
                                                        color="error"
                                                        label="Bloqueado"
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        icon={<CheckCircle />}
                                                        color="success"
                                                        label="Liberado"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {control.attributes?.message || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {control.attributes?.createdAt
                                                    ? format(parseISO(control.attributes.createdAt), 'dd/MM/yyyy HH:mm')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Editar">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => openEditDialog(control)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {controls.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                Nenhum registro de controle encontrado.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {form.id ? 'Editar Controle' : 'Novo Controle'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={0.5}>
                        <Grid item xs={12}>
                            {form.id ? (
                                <TextField
                                    fullWidth
                                    label="Documento"
                                    value={form.document}
                                    disabled
                                />
                            ) : (
                                <Autocomplete
                                    options={subscriberOptions}
                                    value={selectedSubscriber}
                                    loading={loadingSubscribers}
                                    getOptionLabel={(option) => {
                                        const document = option?.attributes?.document || '';
                                        const name = option?.attributes?.name || '';
                                        return `${document}${name ? ` - ${name}` : ''}`;
                                    }}
                                    onChange={(_, value) => {
                                        setSelectedSubscriber(value);
                                        setForm((prev) => ({
                                            ...prev,
                                            document: value?.attributes?.document || ''
                                        }));
                                    }}
                                    onInputChange={(_, value, reason) => {
                                        if (reason !== 'input') return;
                                        clearTimeout(subscriberSearchTimer.current);
                                        subscriberSearchTimer.current = setTimeout(() => {
                                            searchSubscribers(value);
                                        }, 500);
                                    }}
                                    filterOptions={(options) => options}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar subscriber (documento ou nome)"
                                            helperText="Digite ao menos 3 caracteres para buscar na tabela subscribers"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingSubscribers ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(form.status)}
                                        onChange={(e) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                status: e.target.checked
                                            }))
                                        }
                                    />
                                }
                                label={form.status ? 'Blocked: true' : 'Blocked: false'}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Mensagem (opcional)"
                                value={form.message}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        message: e.target.value
                                    }))
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
